import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import type { Tone } from '@/shared/api/types'
import { Icon, type IconName } from './Icon'

/* ── Carte ───────────────────────────────────────────────────── */

export function Card({
  title,
  subtitle,
  tools,
  children,
  flush,
}: {
  title?: string
  subtitle?: string
  tools?: ReactNode
  children: ReactNode
  flush?: boolean
}) {
  return (
    <section className="card">
      {(title || tools) && (
        <div className="card__head">
          <div>
            {title && <h3 className="card__title">{title}</h3>}
            {subtitle && <p className="card__sub">{subtitle}</p>}
          </div>
          {tools && <div className="card__tools">{tools}</div>}
        </div>
      )}
      <div className={`card__body${flush ? ' card__body--flush' : ''}`}>{children}</div>
    </section>
  )
}

/* ── Tuile KPI ───────────────────────────────────────────────── */

export function KpiTile({
  label,
  value,
  unit,
  tone,
  foot,
  spark,
}: {
  label: string
  value: ReactNode
  unit?: string
  tone?: Tone
  foot?: string
  spark?: ReactNode
}) {
  const t = tone && tone !== 'info' && tone !== 'mute' ? ` kpi--${tone}` : ''
  return (
    <div className={`kpi${t}`}>
      <p className="kpi__label">{label}</p>
      <div className="kpi__row">
        <span className="kpi__val">{value}</span>
        {unit && <span className="kpi__unit">{unit}</span>}
      </div>
      {spark}
      {foot && (
        <div className="kpi__foot">
          <span>{foot}</span>
        </div>
      )}
    </div>
  )
}

/* ── Puces ───────────────────────────────────────────────────── */

export function Pill({
  children,
  tone = 'mute',
  plain,
}: {
  children: ReactNode
  tone?: Tone
  plain?: boolean
}) {
  return <span className={`pill pill--${tone}${plain ? ' pill--plain' : ''}`}>{children}</span>
}

export function Tag({ children }: { children: ReactNode }) {
  return <span className="tag">{children}</span>
}

/* ── Boutons ─────────────────────────────────────────────────── */

type Variant = 'default' | 'primary' | 'danger' | 'ok'

/**
 * Bouton du produit.
 *
 * `loading` porte l'action en cours : il désactive le bouton — on ne déclenche
 * pas deux fois une écriture — et remplace l'icône par un témoin animé, à
 * largeur constante pour que la barre d'actions ne se réorganise pas sous le
 * curseur. Aucun écran ne réinvente cet état.
 */
export function Button({
  variant = 'default',
  small,
  icon,
  loading,
  disabled,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  small?: boolean
  icon?: IconName
  loading?: boolean
}) {
  const v = variant === 'default' ? '' : ` btn--${variant}`
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`btn${v}${small ? ' btn--sm' : ''}${loading ? ' btn--busy' : ''}${className ? ` ${className}` : ''}`}
    >
      {loading ? <Spinner /> : icon && <Icon name={icon} />}
      {children}
    </button>
  )
}

/** Témoin d'attente, à la taille d'une icône de bouton. */
export function Spinner({ label }: { label?: string }) {
  return <span className="spinner" role="status" aria-label={label ?? 'Traitement en cours'} />
}

export function IconButton({
  label,
  icon,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; icon: IconName }) {
  return (
    <button
      type="button"
      aria-label={label}
      {...rest}
      className={`icon-btn${className ? ` ${className}` : ''}`}
    >
      <Icon name={icon} />
    </button>
  )
}

export function ButtonRow({ children }: { children: ReactNode }) {
  return <div className="btn-row">{children}</div>
}

/* ── Jauge ───────────────────────────────────────────────────── */

export function Meter({ pct, tone }: { pct: number; tone?: Tone }) {
  const t = tone && tone !== 'info' && tone !== 'mute' ? ` meter--${tone}` : ''
  return (
    <div className={`meter${t}`}>
      <i style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  )
}

/* ── Bandeau ─────────────────────────────────────────────────── */

export function Note({
  tone,
  title,
  children,
  action,
}: {
  tone: 'bad' | 'warn' | 'info'
  /** Surtitre facultatif : un message déjà rédigé pour l'utilisateur s'en passe. */
  title?: string
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div className={`note note--${tone}`}>
      <Icon name={tone === 'info' ? 'bell' : 'warn'} />
      <div>
        {title && <b className="note__t">{title}</b>}
        {children}
      </div>
      {action && <div className="note__act">{action}</div>}
    </div>
  )
}

/* ── États ───────────────────────────────────────────────────── */

export function EmptyState({
  title,
  message,
  action,
  icon = 'check',
}: {
  title: string
  message: string
  action?: ReactNode
  icon?: IconName
}) {
  return (
    <div className="empty">
      <Icon name={icon} size={34} />
      <b>{title}</b>
      <p>{message}</p>
      {action}
    </div>
  )
}

export function Skeleton({ lines = 4 }: { lines?: number }) {
  return (
    <div className="stack" style={{ padding: 16 }}>
      {Array.from({ length: lines }, (_, i) => (
        <div
          key={i}
          className="skel"
          style={{ height: i === 0 ? 28 : 16, width: `${100 - i * 9}%` }}
        />
      ))}
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="empty">
      <Icon name="warn" size={34} />
      <b>Impossible de charger ces données</b>
      <p>{message}</p>
      {onRetry && (
        <Button variant="primary" onClick={onRetry}>
          Réessayer
        </Button>
      )}
    </div>
  )
}

/* ── Définitions ─────────────────────────────────────────────── */

export interface Def {
  k: string
  v: ReactNode
  mono?: boolean
  tone?: string
}

export function Defs({ items }: { items: Def[] }) {
  return (
    <div className="defs">
      {items.map((d) => (
        <div key={d.k}>
          <p className="def__k">{d.k}</p>
          <p
            className={`def__v${d.mono ? ' num' : ''}`}
            style={d.tone ? { color: `var(--${d.tone})` } : undefined}
          >
            {d.v}
          </p>
        </div>
      ))}
    </div>
  )
}

/* ── Filtres ─────────────────────────────────────────────────── */

export function SearchInput({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="search">
      <Icon name="search" />
      <input
        className="input"
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Filters({ children }: { children: ReactNode }) {
  return <div className="filters">{children}</div>
}

/* ── Champs bruts ────────────────────────────────────────────── */

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`input${className ? ` ${className}` : ''}`} />
}

export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`input${className ? ` ${className}` : ''}`} />
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`input${className ? ` ${className}` : ''}`} />
}

/* ── Grilles ─────────────────────────────────────────────────── */

type Cols = 2 | 3 | 4 | 5 | '2-1' | '1-2' | '3-2'

export function Grid({ cols, children, gap }: { cols: Cols; children: ReactNode; gap?: number }) {
  const cls = typeof cols === 'number' ? `g${cols}` : `g-${cols}`
  return (
    <div className={`grid ${cls}`} style={gap ? { gap } : undefined}>
      {children}
    </div>
  )
}

export function Stack({ children, gap }: { children: ReactNode; gap?: number }) {
  return (
    <div className="stack" style={gap ? { gap } : undefined}>
      {children}
    </div>
  )
}

export function Inline({ children, gap }: { children: ReactNode; gap?: number }) {
  return (
    <div className="inline" style={gap ? { gap } : undefined}>
      {children}
    </div>
  )
}

export function Hint({ children }: { children: ReactNode }) {
  return <p className="hint">{children}</p>
}
